/**
 * Примитивы SVG в координатах макета — то же, что pdf-kit делает для PDF.
 *
 * ЗАЧЕМ ВТОРОЙ РЕНДЕР. PDF отдают клиенту, а дизайнер открывает отчёт в
 * Figma и правит: подпись, цифру, порядок слайдов. Figma читает SVG слоями
 * и держит текст текстом — из PDF же выходят кривые, в которых править
 * нечего. Геометрия у обоих одна (layout.ts), расходиться им нельзя.
 */
import type { jsPDF } from 'jspdf'

import type { FontKey, LogoAnchor } from './layout'
import { baselineOf, measureText, registerFonts, type TextStyle } from './pdf-kit'

export type Box = { x: number; y: number; w: number; h: number }

/** Шрифты макета — все из Google Fonts, поэтому в Figma подставятся сами. */
const FAMILY: Record<FontKey, { name: string; weight: number }> = {
  rubik: { name: 'Rubik', weight: 400 },
  rubikSemiBold: { name: 'Rubik', weight: 600 },
  golos: { name: 'Golos Text', weight: 400 },
  golosSemiBold: { name: 'Golos Text', weight: 600 },
  oswaldBold: { name: 'Oswald', weight: 700 },
  bricolageSemiBold: { name: 'Bricolage Grotesque', weight: 600 },
  // Экран статистики набран системным шрифтом iOS, которого в Figma нет:
  // берём Inter — он есть всегда и по рисунку это тот же гротеск.
  sf: { name: 'Inter', weight: 600 },
}

/**
 * Линейка: ширину строки меряем тем же движком, что и PDF, иначе центровка
 * в SVG разойдётся с центровкой в PDF.
 */
export function createRuler(
  JsPDF: typeof jsPDF,
  fonts: Record<FontKey, string>,
): jsPDF {
  const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: [1920, 1080] })
  registerFonts(doc, fonts)
  return doc
}

export function widthOf(ruler: jsPDF, text: string, font: FontKey, size: number, tracking = 0): number {
  return measureText(ruler, text, font, size, tracking)
}

const escapeText = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const round = (n: number) => Math.round(n * 1000) / 1000

/** Холст слайда: копит элементы и отдаёт готовый файл. */
export class Slide {
  private parts: string[] = []
  private defs: string[] = []
  private clips = 0

  /** Линейка открыта: ширину строки меряют и снаружи, подгоняя плашки. */
  readonly ruler: jsPDF
  readonly width: number
  readonly height: number

  constructor(ruler: jsPDF, width: number, height: number) {
    this.ruler = ruler
    this.width = width
    this.height = height
  }

  add(markup: string): void {
    this.parts.push(markup)
  }

  rect(box: Box, fill: string, opacity = 1): void {
    this.add(
      `<rect x="${round(box.x)}" y="${round(box.y)}" width="${round(box.w)}" height="${round(box.h)}" fill="${fill}"` +
        `${opacity < 1 ? ` fill-opacity="${opacity}"` : ''}/>`,
    )
  }

  roundedRect(box: Box, r: number, fill: string, opacity = 1): void {
    this.add(
      `<rect x="${round(box.x)}" y="${round(box.y)}" width="${round(box.w)}" height="${round(box.h)}" rx="${round(r)}"` +
        ` fill="${fill}"${opacity < 1 ? ` fill-opacity="${opacity}"` : ''}/>`,
    )
  }

  /** Обводка внутрь фигуры, как «Inside» в Figma. */
  strokeRoundedRectInside(box: Box, r: number, width: number, color: string, opacity = 1): void {
    const half = width / 2
    this.add(
      `<rect x="${round(box.x + half)}" y="${round(box.y + half)}" width="${round(box.w - width)}"` +
        ` height="${round(box.h - width)}" rx="${round(Math.max(0, r - half))}" fill="none" stroke="${color}"` +
        ` stroke-width="${round(width)}"${opacity < 1 ? ` stroke-opacity="${opacity}"` : ''}/>`,
    )
  }

  line(x1: number, y1: number, x2: number, y2: number, color: string, width: number): void {
    this.add(
      `<line x1="${round(x1)}" y1="${round(y1)}" x2="${round(x2)}" y2="${round(y2)}" stroke="${color}"` +
        ` stroke-width="${round(width)}"/>`,
    )
  }

  /**
   * Строка текста. Левый край считаем сами: с letter-spacing браузер добавляет
   * интервал и после последнего знака, и text-anchor уехал бы вбок.
   */
  text(
    value: string,
    style: TextStyle,
    pos: { x: number; top?: number; baseline?: number; align?: 'left' | 'center' | 'right' },
  ): { left: number; width: number } {
    const tracking = style.tracking ?? 0
    const width = widthOf(this.ruler, value, style.font, style.size, tracking)
    const left =
      pos.align === 'center' ? pos.x - width / 2 : pos.align === 'right' ? pos.x - width : pos.x
    const y = pos.baseline ?? baselineOf(style, pos.top ?? 0)
    // Имя шрифта одним словом, без запасных через запятую: Figma такой
    // список не разбирает и рисует пустую строку вместо текста.
    const f = FAMILY[style.font]
    const family = f.name
    this.add(
      `<text x="${round(left)}" y="${round(y)}" fill="${style.color}" font-family="${family}"` +
        ` font-size="${round(style.size)}" font-weight="${f.weight}"` +
        `${tracking ? ` letter-spacing="${round(tracking)}"` : ''}` +
        ` xml:space="preserve">${escapeText(value)}</text>`,
    )
    return { left, width }
  }

  image(href: string, box: Box): void {
    this.add(
      `<image x="${round(box.x)}" y="${round(box.y)}" width="${round(box.w)}" height="${round(box.h)}"` +
        ` preserveAspectRatio="none" href="${href}"/>`,
    )
  }

  /** Заполняет область с обрезкой — как object-fit: cover. */
  imageCover(href: string, size: { width: number; height: number }, box: Box, r: number): void {
    const scale = Math.max(box.w / size.width, box.h / size.height)
    const w = size.width * scale
    const h = size.height * scale
    const id = this.clip({ ...box }, r)
    this.add(`<g clip-path="url(#${id})">`)
    this.image(href, { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h })
    this.add('</g>')
  }

  /** Вписывает целиком — как object-fit: contain. */
  imageContain(href: string, size: { width: number; height: number }, box: Box, anchor: LogoAnchor): void {
    const scale = Math.min(box.w / size.width, box.h / size.height)
    const w = size.width * scale
    const h = size.height * scale
    const x = anchor === 'left' ? box.x : anchor === 'right' ? box.x + box.w - w : box.x + (box.w - w) / 2
    this.image(href, { x, y: box.y + (box.h - h) / 2, w, h })
  }

  clip(box: Box, r: number): string {
    const id = `clip${++this.clips}`
    this.defs.push(
      `<clipPath id="${id}"><rect x="${round(box.x)}" y="${round(box.y)}" width="${round(box.w)}"` +
        ` height="${round(box.h)}" rx="${round(r)}"/></clipPath>`,
    )
    return id
  }

  open(markup: string): void {
    this.add(markup)
  }

  close(tag: string): void {
    this.add(`</${tag}>`)
  }

  toFile(title: string): string {
    return (
      `<?xml version="1.0" encoding="UTF-8"?>\n` +
      `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"` +
      ` width="${this.width}" height="${this.height}" viewBox="0 0 ${this.width} ${this.height}">\n` +
      `<title>${escapeText(title)}</title>\n` +
      (this.defs.length ? `<defs>\n${this.defs.join('\n')}\n</defs>\n` : '') +
      this.parts.join('\n') +
      `\n</svg>\n`
    )
  }
}

/** Картинка внутрь файла: Figma должна открывать один файл, без соседних. */
export function dataUri(bytes: Uint8Array, format: 'PNG' | 'JPEG'): string {
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  const type = format === 'PNG' ? 'image/png' : 'image/jpeg'
  return `data:${type};base64,${btoa(binary)}`
}
